import type { components } from "@brandex/api-client";
import { formatTaka, type MessageKey } from "@brandex/i18n";
import { Alert, Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Field, Input, Select, Spinner } from "@brandex/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { useBrandex, useT } from "../context";
import { errorMessage } from "../errors";
import { useCurrentOrg } from "../client-scope";
import { usePractice } from "../queries";
import { shootWhen } from "./content";

type Settings = components["schemas"]["BookingSettingsDto"];
type Appointment = components["schemas"]["AppointmentDto"];
type TimeOff = components["schemas"]["TimeOffDto"];
type PaymentStatus = NonNullable<Appointment["payment"]>["status"];

const PAYMENT_TONE: Record<PaymentStatus, "success" | "warning" | "neutral"> = {
  pending: "warning",
  paid: "success",
  failed: "neutral",
  expired: "neutral",
  refund_due: "warning",
};

const STATUS_TONE: Record<Appointment["status"], "success" | "warning" | "neutral" | "accent"> = {
  pending: "warning",
  confirmed: "success",
  declined: "neutral",
  cancelled: "neutral",
};

const settingsKey = (org: string) => ["orgs", org, "booking-settings"] as const;
const appointmentsKey = (org: string) => ["orgs", org, "appointments"] as const;
const timeOffKey = (org: string) => ["orgs", org, "time-off"] as const;

/**
 * Bookings (F-08): booking method, fee and notifications, the portfolio
 * booking switch, appointments to confirm or decline, time off and weekly
 * availability.
 */
export function BookingsView() {
  const t = useT();
  const orgs = useCurrentOrg();
  const orgId = orgs.orgId;
  if (orgs.isPending) return <Spinner label={t("common.loading")} />;
  if (orgs.isError) return <Alert tone="error">{errorMessage(orgs.error, t)}</Alert>;
  if (orgId === undefined) return null;
  return (
    <div className="mx-auto grid max-w-5xl gap-6">
      <header>
        <h1 className="font-display text-3xl font-bold tracking-tight">{t("bookings.title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("bookings.subtitle")}</p>
      </header>
      <SettingsCard orgId={orgId} />
      <AppointmentsCard orgId={orgId} />
      <TimeOffCard orgId={orgId} />
      <AvailabilityCard orgId={orgId} />
    </div>
  );
}

function SettingsCard({ orgId }: { orgId: string }) {
  const t = useT();
  const { api } = useBrandex();
  const settings = useQuery<Settings>({
    queryKey: settingsKey(orgId),
    queryFn: async () => {
      const { data } = await api.http.GET("/v1/orgs/{org_id}/booking-settings", { params: { path: { org_id: orgId } } });
      if (data === undefined) throw new Error("Empty response");
      return data;
    },
  });
  if (settings.isPending) return <Spinner label={t("common.loading")} />;
  if (settings.isError) return <Alert tone="error">{errorMessage(settings.error, t)}</Alert>;
  return <SettingsForm orgId={orgId} initial={settings.data} />;
}

/** `+8801712345678` as people write it, `01712-345678`; others unchanged. */
export function nationalPhone(e164: string | null | undefined): string | null {
  if (e164 == null) return null;
  const m = /^\+880(1\d{3})(\d{6})$/.exec(e164);
  return m ? `0${m[1] ?? ""}-${m[2] ?? ""}` : e164;
}

/** Saved numbers shown in the form the doctor typed them. */
const forForm = (v: Settings): Settings => ({
  ...v,
  notify_whatsapp: nationalPhone(v.notify_whatsapp),
  contact_phone: nationalPhone(v.contact_phone),
  contact_whatsapp: nationalPhone(v.contact_whatsapp),
});

function SettingsForm({ orgId, initial }: { orgId: string; initial: Settings }) {
  const t = useT();
  const { api } = useBrandex();
  const qc = useQueryClient();
  const [s, setS] = useState<Settings>(() => forForm(initial));
  const save = useMutation({
    mutationFn: async (body: Settings) => {
      const { data } = await api.http.PUT("/v1/orgs/{org_id}/booking-settings", {
        params: { path: { org_id: orgId } },
        body: {
          ...body,
          calendly_url: body.method === "calendly" ? body.calendly_url ?? null : null,
          // Only BrandEx's own booking takes payments.
          online_payment: body.method === "built_in" && body.online_payment,
        },
      });
      if (data === undefined) throw new Error("Empty response");
      return data;
    },
    onSuccess: (data) => {
      qc.setQueryData(settingsKey(orgId), data);
      setS(forForm(data));
    },
  });
  const change = (patch: Partial<Settings>) => {
    save.reset();
    setS((v) => ({ ...v, ...patch }));
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("bookings.method")}</CardTitle>
      </CardHeader>
      <CardContent>
        <form className="grid gap-5" onSubmit={(e) => { e.preventDefault(); save.mutate(s); }}>
          <fieldset className="grid gap-3 md:grid-cols-3">
            <legend className="sr-only">{t("bookings.method")}</legend>
            {(["contact", "built_in", "calendly"] as const).map((m) => (
              <label key={m} className={`grid cursor-pointer gap-1 rounded-[var(--radius-lg)] border p-4 ${s.method === m ? "border-primary ring-2 ring-primary/30" : "border-border"}`}>
                <span className="flex items-center gap-2 font-medium">
                  <input type="radio" name="method" checked={s.method === m} onChange={() => change({ method: m })} />
                  {t(`bookings.method.${m}` as MessageKey)}
                </span>
                <span className="text-sm text-muted-foreground">{t(`bookings.method.${m}.hint` as MessageKey)}</span>
              </label>
            ))}
          </fieldset>
          {s.method === "calendly" ? (
            <Field label={t("bookings.calendly")}>
              <Input type="url" placeholder="https://calendly.com/…" value={s.calendly_url ?? ""} onChange={(e) => change({ calendly_url: e.target.value })} />
            </Field>
          ) : null}
          {s.method === "contact" ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t("bookings.contactPhone")} hint={t("bookings.contactPhoneHint")}>
                <Input type="tel" inputMode="tel" autoComplete="tel" value={s.contact_phone ?? ""} onChange={(e) => change({ contact_phone: e.target.value })} />
              </Field>
              <div className="grid gap-1.5">
                <Field label={t("bookings.contactWhatsapp")} hint={t("bookings.contactWhatsappHint")}>
                  <Input type="tel" inputMode="tel" value={s.contact_whatsapp ?? ""} onChange={(e) => change({ contact_whatsapp: e.target.value })} />
                </Field>
                {(s.contact_phone ?? "").trim() !== "" && s.contact_whatsapp !== s.contact_phone ? (
                  <Button type="button" size="sm" variant="ghost" className="w-fit" onClick={() => change({ contact_whatsapp: s.contact_phone ?? null })}>
                    {t("bookings.sameNumber")}
                  </Button>
                ) : null}
              </div>
            </div>
          ) : null}
          {s.method === "built_in" ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t("bookings.whatsapp")} hint={t("bookings.whatsappHint")}>
                <Input type="tel" inputMode="tel" value={s.notify_whatsapp ?? ""} onChange={(e) => change({ notify_whatsapp: e.target.value })} />
              </Field>
              <Field label={t("bookings.email")} hint={t("bookings.emailHint")}>
                <Input type="email" value={s.notify_email ?? ""} onChange={(e) => change({ notify_email: e.target.value })} />
              </Field>
            </div>
          ) : null}
          <label className="flex items-start gap-3">
            <input type="checkbox" className="mt-1" checked={s.public_booking} onChange={(e) => change({ public_booking: e.target.checked })} />
            <span>
              <span className="font-medium">{t("bookings.public")}</span>
              <span className="block text-sm text-muted-foreground">
                {t(s.method === "calendly" ? "bookings.publicCalendly" : s.method === "contact" ? "bookings.publicContact" : "bookings.publicHint")}
              </span>
            </span>
          </label>
          {s.method === "contact" && s.public_booking && (s.contact_phone ?? "").trim() === "" && (s.contact_whatsapp ?? "").trim() === "" ? (
            <Alert tone="info">{t("bookings.contactNeeded")}</Alert>
          ) : null}
          {s.method !== "calendly" ? (
            <div className="grid gap-3 rounded-[var(--radius-lg)] border border-border p-4">
              <Field label={t("bookings.fee")} hint={t("bookings.feeHint")}>
                <Input
                  inputMode="numeric"
                  className="max-w-40"
                  value={s.fee_paisa != null ? String(s.fee_paisa / 100) : ""}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, "");
                    change({ fee_paisa: digits === "" ? null : Number(digits) * 100, ...(digits === "" ? { online_payment: false } : {}) });
                  }}
                />
              </Field>
              {s.method === "built_in" ? (
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-1"
                  disabled={!s.payments_available || s.fee_paisa == null}
                  checked={s.online_payment}
                  onChange={(e) => change({ online_payment: e.target.checked })}
                />
                <span>
                  <span className="font-medium">{t("bookings.onlinePayment")}</span>
                  <span className="block text-sm text-muted-foreground">
                    {s.payments_available ? `${t("bookings.onlinePaymentHint")} ${t("bookings.onlinePaymentSandbox")}` : t("bookings.onlinePaymentOff")}
                  </span>
                </span>
              </label>
              ) : null}
            </div>
          ) : null}
          {s.method === "built_in" ? (
            <label className="flex items-start gap-3">
              <input type="checkbox" className="mt-1" checked={s.auto_confirm} onChange={(e) => change({ auto_confirm: e.target.checked })} />
              <span>
                <span className="font-medium">{t("bookings.autoConfirm")}</span>
                <span className="block text-sm text-muted-foreground">{t("bookings.autoConfirmHint")}</span>
              </span>
            </label>
          ) : null}
          {save.isError ? <Alert tone="error">{errorMessage(save.error, t)}</Alert> : null}
          {save.isSuccess ? <Alert tone="success">{t("bookings.saved")}</Alert> : null}
          <div><Button type="submit" loading={save.isPending}>{t("bookings.save")}</Button></div>
        </form>
      </CardContent>
    </Card>
  );
}

function AppointmentsCard({ orgId }: { orgId: string }) {
  const t = useT();
  const { api, locale } = useBrandex();
  const qc = useQueryClient();
  const list = useQuery<Appointment[]>({
    queryKey: appointmentsKey(orgId),
    queryFn: async () => {
      const { data } = await api.http.GET("/v1/orgs/{org_id}/appointments", { params: { path: { org_id: orgId } } });
      if (data === undefined) throw new Error("Empty response");
      return data;
    },
  });
  const decide = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: "confirmed" | "declined" | "cancelled" }) => {
      await api.http.POST("/v1/orgs/{org_id}/appointments/{appointment_id}/status", {
        params: { path: { org_id: orgId, appointment_id: id } },
        body: { status },
      });
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: appointmentsKey(orgId) }),
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("bookings.appointments")}</CardTitle>
        <CardDescription>{t("bookings.appointmentsHint")}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3">
        {list.isPending ? <Spinner label={t("common.loading")} /> : null}
        {list.isError ? <Alert tone="error">{errorMessage(list.error, t)}</Alert> : null}
        {decide.isError ? <Alert tone="error">{errorMessage(decide.error, t)}</Alert> : null}
        {list.data?.length === 0 ? <p className="text-sm text-muted-foreground">{t("bookings.none")}</p> : null}
        {list.data !== undefined && list.data.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr><th className="py-2">{t("bookings.when")}</th><th>{t("bookings.patient")}</th><th>{t("bookings.chamber")}</th><th>{t("bookings.reason")}</th><th>{t("bookings.status")}</th><th /></tr>
              </thead>
              <tbody className="divide-y divide-border">
                {list.data.map((a) => (
                  <tr key={a.id} className="align-top">
                    <td className="py-3">
                      {shootWhen(locale, a.starts_at)}
                      <span className="block text-xs text-muted-foreground">{t("bookings.reference")} {a.reference}</span>
                    </td>
                    <td>
                      <p className="font-medium">{a.patient_name}</p>
                      <a className="text-xs text-primary hover:underline" href={`tel:${a.patient_phone}`}>{a.patient_phone}</a>
                      {a.patient_email ? <a className="block text-xs text-muted-foreground hover:underline" href={`mailto:${a.patient_email}`}>{a.patient_email}</a> : null}
                    </td>
                    <td>{a.chamber_name ?? "—"}</td>
                    <td>{a.reason || "—"}</td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        <Badge tone={STATUS_TONE[a.status]}>{t(`bookings.state.${a.status}` as MessageKey)}</Badge>
                        {a.payment ? (
                          <Badge tone={PAYMENT_TONE[a.payment.status]}>
                            {t(`bookings.payment.${a.payment.status}` as MessageKey, { amount: formatTaka(locale, a.payment.amount_paisa) })}
                          </Badge>
                        ) : null}
                      </div>
                      {a.cancelled_by ? <span className="block text-xs text-muted-foreground">{t(`bookings.cancelledBy.${a.cancelled_by}` as MessageKey)}</span> : null}
                      {a.reschedules > 0 ? <span className="block text-xs text-muted-foreground">{t("bookings.moved", { n: a.reschedules })}</span> : null}
                    </td>
                    <td className="text-right">
                      {a.status === "pending" ? (
                        <>
                          <Button size="sm" variant="accent" disabled={a.payment?.status === "pending"} aria-label={t("bookings.confirmFor", { name: a.patient_name })} onClick={() => decide.mutate({ id: a.id, status: "confirmed" })}>{t("bookings.confirm")}</Button>
                          <Button size="sm" variant="ghost" aria-label={t("bookings.declineFor", { name: a.patient_name })} onClick={() => decide.mutate({ id: a.id, status: "declined" })}>{t("bookings.decline")}</Button>
                        </>
                      ) : a.status === "confirmed" ? (
                        <Button size="sm" variant="ghost" aria-label={t("bookings.cancelFor", { name: a.patient_name })} onClick={() => decide.mutate({ id: a.id, status: "cancelled" })}>{t("bookings.cancel")}</Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** `datetime-local` value (Bangladesh time) → RFC 3339 with the +06:00 offset. */
export function dhakaInstant(local: string): string {
  return `${local.length === 16 ? `${local}:00` : local}+06:00`;
}

function TimeOffCard({ orgId }: { orgId: string }) {
  const t = useT();
  const { api, locale } = useBrandex();
  const qc = useQueryClient();
  const practice = usePractice(orgId);
  const list = useQuery<TimeOff[]>({
    queryKey: timeOffKey(orgId),
    queryFn: async () => {
      const { data } = await api.http.GET("/v1/orgs/{org_id}/time-off", { params: { path: { org_id: orgId } } });
      if (data === undefined) throw new Error("Empty response");
      return data;
    },
  });
  const [chamber, setChamber] = useState("");
  const [from, setFrom] = useState("");
  const [until, setUntil] = useState("");
  const [note, setNote] = useState("");
  const badRange = from !== "" && until !== "" && until <= from;
  const add = useMutation({
    mutationFn: async () => {
      const { data } = await api.http.POST("/v1/orgs/{org_id}/time-off", {
        params: { path: { org_id: orgId } },
        body: { chamber_id: chamber === "" ? null : chamber, starts_at: dhakaInstant(from), ends_at: dhakaInstant(until), note: note.trim() },
      });
      if (data === undefined) throw new Error("Empty response");
      return data;
    },
    onSuccess: () => {
      setFrom("");
      setUntil("");
      setNote("");
      void qc.invalidateQueries({ queryKey: timeOffKey(orgId) });
    },
  });
  const remove = useMutation({
    mutationFn: async (id: string) => {
      await api.http.DELETE("/v1/orgs/{org_id}/time-off/{time_off_id}", { params: { path: { org_id: orgId, time_off_id: id } } });
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: timeOffKey(orgId) }),
  });
  const chamberName = (id: string | null | undefined) =>
    id == null ? t("bookings.timeOff.allChambers") : (practice.data?.chambers.find((c) => c.id === id)?.name ?? "—");
  const range = (x: TimeOff) => `${shootWhen(locale, x.starts_at)} – ${shootWhen(locale, x.ends_at)}`;
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("bookings.timeOff")}</CardTitle>
        <CardDescription>{t("bookings.timeOffHint")}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        <form
          className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 lg:items-end"
          onSubmit={(e) => {
            e.preventDefault();
            if (from !== "" && until !== "" && !badRange) add.mutate();
          }}
        >
          <Field label={t("bookings.timeOff.chamber")}>
            <Select value={chamber} onChange={(e) => setChamber(e.target.value)}>
              <option value="">{t("bookings.timeOff.allChambers")}</option>
              {practice.data?.chambers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </Field>
          <Field label={t("bookings.timeOff.from")}>
            <Input type="datetime-local" value={from} onChange={(e) => { add.reset(); setFrom(e.target.value); }} />
          </Field>
          <Field label={t("bookings.timeOff.until")} error={badRange ? t("bookings.timeOff.badRange") : undefined}>
            <Input type="datetime-local" value={until} onChange={(e) => { add.reset(); setUntil(e.target.value); }} />
          </Field>
          <Field label={t("bookings.timeOff.note")}>
            <Input maxLength={120} value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
          <div className="sm:col-span-2 lg:col-span-4">
            <Button type="submit" disabled={from === "" || until === "" || badRange} loading={add.isPending}>{t("bookings.timeOff.add")}</Button>
          </div>
        </form>
        {add.isError ? <Alert tone="error">{errorMessage(add.error, t)}</Alert> : null}
        {add.data !== undefined && add.data.affected.length > 0 ? (
          <Alert tone="info">
            {t("bookings.timeOff.affected", { n: add.data.affected.length })}
            <ul className="mt-2 grid gap-1">
              {add.data.affected.map((a) => <li key={a.id}>{shootWhen(locale, a.starts_at)} · {a.patient_name} · <a className="underline" href={`tel:${a.patient_phone}`}>{a.patient_phone}</a></li>)}
            </ul>
          </Alert>
        ) : null}
        {list.isError ? <Alert tone="error">{errorMessage(list.error, t)}</Alert> : null}
        {remove.isError ? <Alert tone="error">{errorMessage(remove.error, t)}</Alert> : null}
        {list.data?.length === 0 ? <p className="text-sm text-muted-foreground">{t("bookings.timeOff.none")}</p> : null}
        <ul className="grid gap-2">
          {list.data?.map((x) => (
            <li key={x.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-sm">
              <span>
                <span className="font-medium">{range(x)}</span>
                <span className="block text-muted-foreground">{chamberName(x.chamber_id)}{x.note ? ` · ${x.note}` : ""}</span>
              </span>
              <Button size="sm" variant="ghost" aria-label={t("bookings.timeOff.removeFor", { range: range(x) })} loading={remove.isPending && remove.variables === x.id} onClick={() => remove.mutate(x.id)}>
                {t("bookings.timeOff.remove")}
              </Button>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function AvailabilityCard({ orgId }: { orgId: string }) {
  const t = useT();
  const { nav } = useBrandex();
  const practice = usePractice(orgId);
  if (!practice.data) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("bookings.availability")}</CardTitle>
        <CardDescription>
          {t("bookings.availabilityHint")}{" "}
          <nav.Link href="/app/profile" className="font-medium underline">{t("profile.title")}</nav.Link>
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="grid gap-2 text-sm">
          {practice.data.chambers.map((c) => (
            <li key={c.id} className="rounded-md border border-border px-3 py-2">
              <span className="font-medium">{c.name}</span> · <span className="text-muted-foreground">{c.address}</span>
              <span className="block text-muted-foreground">
                {c.days.map((d) => t(`day.${d}` as MessageKey)).join(", ")} · {c.opens}–{c.closes} · {t("onboarding.minutesValue", { n: c.slot_minutes })}
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
